import { useState, useMemo } from "react";
import { X, Check, AlertTriangle, Users, MapPin, Activity, Zap, Timer, Target, ChevronDown, ChevronUp, Info } from "lucide-react";

const METRIC_LABELS = {
  distance: { label: "Distance", unit: "m", icon: Activity },
  duration: { label: "Duration", unit: "min", icon: Timer },
  hsr: { label: "HSR", unit: "m", icon: Zap },
  msr: { label: "MSR", unit: "m", icon: Zap },
  sprint80_number: { label: "Sprints", unit: "", icon: Target },
};

const METRIC_KEYS = ["distance", "duration", "hsr", "msr", "sprint80_number"];

// Calcule l'écart relatif entre la valeur matchée et la cible (en %)
function getRelativeDeviation(matchValue, targetValue) {
  if (!matchValue || !targetValue || targetValue === 0) return null;
  return Math.abs((matchValue - targetValue) / targetValue) * 100;
}

// Returns color based on deviation
function getDeviationColor(deviation, isDarkMode) {
  if (deviation === null) return isDarkMode ? "text-gray-400" : "text-gray-600";
  if (deviation < 30) return "text-emerald-500"; // Excellent
  if (deviation < 50) return "text-amber-500";   // Acceptable
  return "text-red-500";                          // Far
}

// Returns score color (lower = better)
function getScoreColor(score, isDarkMode) {
  if (score === null || score === undefined) return isDarkMode ? "text-gray-400" : "text-gray-600";
  if (score <= 1.5) return "text-emerald-500"; // Excellent
  if (score <= 2.5) return "text-amber-500";   // Good
  return "text-red-500";                        // Far
}

function PlayerRow({ player, isDarkMode, expanded, onToggle }) {
  const ref = player.references || {};
  const target = player.target_metrics || {};
  const game = player.matched_game;
  const isFallback = player.reference_source === "position_fallback";

  const row = isDarkMode ? "border-gray-700 hover:bg-gray-800/50" : "border-gray-200 hover:bg-gray-50";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-600";

  return (
    <div className={`border-b last:border-b-0 ${row} transition-colors`}>
      {/* Summary row */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
        onClick={onToggle}
      >
        <div className="w-8 flex items-center justify-center">
          {expanded ? <ChevronUp size={14} className={muted} /> : <ChevronDown size={14} className={muted} />}
        </div>

        {/* Player info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium truncate">
              {player.first_name} {player.last_name}
            </span>
            {isFallback && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500 font-medium">
                Estimation
              </span>
            )}
          </div>
          <div className={`flex items-center gap-1 text-xs ${muted}`}>
            <MapPin size={10} />
            <span>{player.position || "N/A"}</span>
          </div>
        </div>

        {/* Quick metrics display - Affiche les valeurs réelles matchées (pas les cibles) */}
        <div className="flex items-center gap-4">
          {METRIC_KEYS.slice(0, 3).map((k) => {
            const cfg = METRIC_LABELS[k];
            const matchValue = game?.[k];
            const targetValue = target[k];
            const deviation = getRelativeDeviation(matchValue, targetValue);
            const color = game ? getDeviationColor(deviation, isDarkMode) : (isDarkMode ? "text-gray-500" : "text-gray-400");
            
            return (
              <div key={k} className="text-center min-w-[60px]">
                <p className={`text-[10px] ${muted}`}>{cfg.label}</p>
                <p className={`text-sm font-semibold ${color}`}>
                  {game ? (matchValue ?? "–") : "–"}
                </p>
              </div>
            );
          })}
        </div>

        {/* Match status */}
        <div className="w-20 text-center">
          {game ? (
            <span className="text-[10px] px-2 py-1 rounded bg-emerald-500/20 text-emerald-500 font-medium">
              Matched
            </span>
          ) : (
            <span className="text-[10px] px-2 py-1 rounded bg-red-500/20 text-red-500 font-medium">
              No match
            </span>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className={`px-4 pb-4 pl-12 ${isDarkMode ? "bg-gray-800/30" : "bg-gray-50/50"}`}>
          <div className="grid grid-cols-5 gap-3 mt-2">
            {METRIC_KEYS.map((k) => {
              const cfg = METRIC_LABELS[k];
              const Icon = cfg.icon;
              return (
                <div
                  key={k}
                  className={`rounded-xl border p-3 ${isDarkMode ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-white"}`}
                >
                  <div className={`flex items-center gap-1.5 text-xs ${muted} mb-2`}>
                    <Icon size={12} />
                    <span>{cfg.label}</span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between text-xs">
                      <span className={muted}>Ref</span>
                      <span className="font-medium">{ref[k] ?? "–"} {cfg.unit}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className={muted}>Target</span>
                      <span className="font-semibold text-emerald-500">{target[k] ?? "–"} {cfg.unit}</span>
                    </div>
                    {game && (
                      <div className="flex justify-between text-xs">
                        <span className={muted}>Match</span>
                        <span className={`font-medium ${getDeviationColor(getRelativeDeviation(game[k], target[k]), isDarkMode)}`}>
                          {game[k] ?? "–"} {cfg.unit}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {game && (
            <div className={`mt-3 rounded-lg border p-2 text-xs ${isDarkMode ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-gray-50"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <span className={muted}>Matched game: </span>
                  <span className="font-medium">{game.name || "–"}</span>
                  {game.date && <span className={`ml-2 ${muted}`}>({game.date})</span>}
                  {game.from_fallback_player && (
                    <span className="ml-2 text-amber-500">(via same position player)</span>
                  )}
                </div>
                {game.score !== undefined && (
                  <div className="flex items-center gap-1">
                    <span className={muted}>Score:</span>
                    <span className={`font-semibold ${getScoreColor(game.score, isDarkMode)}`}>
                      {game.score.toFixed(3)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MatchConfirmationModal({
  isDarkMode,
  matchData,
  blockLabel,
  onConfirm,
  onCancel,
  loading,
}) {
  const [expandedPlayers, setExpandedPlayers] = useState(new Set());

  const shell = isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-200";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-600";

  const players = (matchData?.players || []).filter((player) => player?.player_enabled !== false);
  const visibleMatchData = useMemo(
    () => ({ ...matchData, players }),
    [matchData, players]
  );

  const stats = useMemo(() => {
    const matched = players.filter((p) => p.matched_game);
    const fallback = players.filter((p) => p.reference_source === "position_fallback");
    const noMatch = players.filter((p) => !p.matched_game);
    return { matched: matched.length, fallback: fallback.length, noMatch: noMatch.length, total: players.length };
  }, [players]);

  const togglePlayer = (pid) => {
    setExpandedPlayers((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const expandAll = () => {
    setExpandedPlayers(new Set(players.map((p) => p.player_id)));
  };

  const collapseAll = () => {
    setExpandedPlayers(new Set());
  };

  if (!matchData) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} />
      <div className={`relative w-full max-w-4xl max-h-[90vh] rounded-2xl border ${shell} shadow-2xl flex flex-col overflow-hidden`}>
        {/* Header */}
        <div className={`p-5 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-lg font-bold flex items-center gap-2">
                <Users size={20} className="text-emerald-500" />
                Block confirmation: {blockLabel || "Block"}
              </h3>
              <p className={`mt-1 text-sm ${muted}`}>
                Check target metrics for each player before confirming.
              </p>
            </div>
            <button
              onClick={onCancel}
              className={`rounded-xl border p-2 transition ${
                isDarkMode ? "border-gray-700 hover:bg-gray-800" : "border-gray-200 hover:bg-gray-50"
              }`}
            >
              <X size={18} />
            </button>
          </div>

          {/* Stats summary */}
          <div className="flex gap-4 mt-4">
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium ${
              isDarkMode ? "bg-emerald-500/20 text-emerald-400" : "bg-emerald-50 text-emerald-600"
            }`}>
              <Check size={12} />
              {stats.matched} matched
            </div>
            {stats.fallback > 0 && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium ${
                isDarkMode ? "bg-amber-500/20 text-amber-400" : "bg-amber-50 text-amber-600"
              }`}>
                <AlertTriangle size={12} />
                {stats.fallback} estimations
              </div>
            )}
            {stats.noMatch > 0 && (
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium ${
                isDarkMode ? "bg-red-500/20 text-red-400" : "bg-red-50 text-red-600"
              }`}>
                <AlertTriangle size={12} />
                {stats.noMatch} no match
              </div>
            )}
            <div className={`ml-auto flex items-center gap-2 text-xs ${muted}`}>
              <button onClick={expandAll} className="hover:underline">Expand all</button>
              <span>|</span>
              <button onClick={collapseAll} className="hover:underline">Collapse all</button>
            </div>
          </div>

          {/* Légende des couleurs */}
          <div className={`mt-4 p-3 rounded-lg border ${isDarkMode ? "border-gray-700 bg-gray-800/30" : "border-gray-200 bg-gray-50"}`}>
            <div className="flex items-start gap-2">
              <Info size={14} className={`${muted} mt-0.5`} />
              <div className="flex-1">
                <p className={`text-xs font-medium ${muted} mb-2`}>Matching quality indicators</p>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className={muted}>Deviation &lt; 30% (Excellent)</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-amber-500" />
                    <span className={muted}>Deviation 30-50% (Acceptable)</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <span className={muted}>Deviation &gt; 50% (Far)</span>
                  </div>
                  <span className={`${muted} ml-4`}>•</span>
                  <div className="flex items-center gap-1.5">
                    <span className={muted}>Score :</span>
                    <span className="text-emerald-500 font-medium">&lt;1.5</span>
                    <span className="text-amber-500 font-medium">1.5-2.5</span>
                    <span className="text-red-500 font-medium">&gt;2.5</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Players list */}
        <div className="flex-1 overflow-y-auto">
          {/* Column headers */}
          <div className={`flex items-center gap-3 px-4 py-2 text-xs font-medium ${muted} sticky top-0 z-10 ${
            isDarkMode ? "bg-gray-900 border-b border-gray-700" : "bg-white border-b border-gray-200"
          }`}>
            <div className="w-8" />
            <div className="flex-1">Player</div>
            <div className="flex items-center gap-4">
              {METRIC_KEYS.slice(0, 3).map((k) => (
                <div key={k} className="text-center min-w-[60px]">{METRIC_LABELS[k].label}</div>
              ))}
            </div>
            <div className="w-20 text-center">Status</div>
          </div>

          {players.map((p) => (
            <PlayerRow
              key={p.player_id}
              player={p}
              isDarkMode={isDarkMode}
              expanded={expandedPlayers.has(p.player_id)}
              onToggle={() => togglePlayer(p.player_id)}
            />
          ))}
        </div>

        {/* Footer */}
        <div className={`p-4 border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"} flex justify-between items-center`}>
          <p className={`text-xs ${muted}`}>
            {stats.total} players · Mode: {matchData.mode === "per_metric" ? "Per metric" : "Global"}
          </p>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition ${
                isDarkMode
                  ? "bg-gray-800 hover:bg-gray-700 text-gray-300"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
            >
              Edit
            </button>
            <button
              onClick={() => onConfirm(visibleMatchData)}
              disabled={loading}
              className="px-5 py-2 rounded-xl text-sm font-medium bg-emerald-600 hover:bg-emerald-700 text-white transition shadow-lg shadow-emerald-500/20 flex items-center gap-2"
            >
              <Check size={16} />
              {loading ? "Confirming..." : "Confirm block"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
